import React, { useState, useEffect, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Calendar, Clock, Users, ChevronLeft, ChevronRight, Check, Package, AlertCircle, X, CreditCard, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

const API = "/api";
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hr = parseInt(h);
  const ampm = hr >= 12 ? "PM" : "AM";
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
};

// ─── Checkout Modal ───
const PackageCheckoutModal = ({ isOpen, onClose, packageData }) => {
  const [error, setError] = useState(null);

  const fetchClientSecret = useCallback(async () => {
    try {
      setError(null);
      const res = await fetch(`${API}/schedule/packages/${packageData.package_id}/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to create checkout");
      return data.client_secret;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [packageData]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="package-checkout-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[90vh] bg-[#141B2D] rounded-2xl shadow-2xl overflow-hidden mx-4">
        <div className="flex items-center justify-between p-4 border-b border-[#1E293B]">
          <div>
            <h2 className="font-bold text-xl text-white">Complete Your Purchase</h2>
            <p className="text-gray-400 text-sm">{packageData.name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1E293B] rounded-lg transition-colors" data-testid="close-package-checkout">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto" style={{ maxHeight: "calc(90vh - 80px)" }}>
          {error ? (
            <div className="text-center py-8">
              <p className="text-red-400 mb-4">{error}</p>
              <button onClick={() => window.location.reload()} className="bg-primary text-white px-4 py-2 rounded-lg">Try Again</button>
            </div>
          ) : (
            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </div>
      </div>
    </div>
  );
};

const AthleteScheduling = () => {
  const [view, setView] = useState("browse");
  const [slots, setSlots] = useState([]);
  const [sessionTypes, setSessionTypes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [myPurchases, setMyPurchases] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [bookingNote, setBookingNote] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutPkg, setCheckoutPkg] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [slRes, stRes, bkRes, pkRes, mpRes] = await Promise.all([
        fetch(`${API}/schedule/availability`, { credentials: "include" }),
        fetch(`${API}/schedule/session-types`, { credentials: "include" }),
        fetch(`${API}/schedule/bookings`, { credentials: "include" }),
        fetch(`${API}/schedule/packages`, { credentials: "include" }),
        fetch(`${API}/schedule/my-packages`, { credentials: "include" }),
      ]);
      if (slRes.ok) setSlots((await slRes.json()).slots || []);
      if (stRes.ok) setSessionTypes((await stRes.json()).session_types || []);
      if (bkRes.ok) setBookings((await bkRes.json()).bookings || []);
      if (pkRes.ok) setPackages((await pkRes.json()).packages || []);
      if (mpRes.ok) setMyPurchases((await mpRes.json()).purchases || []);
    } catch (err) {
      console.error("Failed to load schedule data", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Check for successful checkout return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const pkgId = params.get("package_id");
    if (sessionId && pkgId) {
      fetch(`${API}/schedule/packages/purchase-status?session_id=${sessionId}`, { credentials: "include" })
        .then(r => r.json())
        .then(data => {
          if (data.status === "active") {
            toast.success("Package purchased successfully!");
            fetchAll();
          }
        });
      // Clean up URL params
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [fetchAll]);

  const openSlots = slots.filter(s => {
    if (s.status === "cancelled") return false;
    const cap = s.capacity || 1;
    const conf = s.confirmed_count || 0;
    return conf < cap; // Show slots that still have room
  });

  const getWeekDates = () => {
    const today = new Date();
    today.setDate(today.getDate() + weekOffset * 7 - today.getDay() + 1);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      return d.toISOString().slice(0, 10);
    });
  };
  const weekDates = getWeekDates();
  const slotsByDate = {};
  openSlots.forEach(s => {
    if (!slotsByDate[s.date]) slotsByDate[s.date] = [];
    slotsByDate[s.date].push(s);
  });

  const handleBook = async () => {
    if (!selectedSlot) return;
    try {
      const res = await fetch(`${API}/schedule/book`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          slot_id: selectedSlot.slot_id,
          session_type_id: selectedTypeId || null,
          note: bookingNote,
          is_recurring: isRecurring,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        const recurringMsg = data.recurring_created > 0 ? ` + ${data.recurring_created} recurring weeks` : "";
        toast.success(`Booking request sent!${recurringMsg} The coach will confirm.`);
        setSelectedSlot(null);
        setBookingNote("");
        setSelectedTypeId("");
        setIsRecurring(false);
        fetchAll();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Booking failed");
      }
    } catch {
      toast.error("Failed to book");
    }
  };

  const pendingBookings = bookings.filter(b => b.status === "pending");
  const confirmedBookings = bookings.filter(b => b.status === "confirmed");
  const totalRemaining = myPurchases.reduce((sum, p) => sum + (p.sessions_remaining || 0), 0);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="athlete-scheduling">
      {/* Purchased sessions banner */}
      {totalRemaining > 0 && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center gap-3" data-testid="sessions-remaining-banner">
          <ShoppingBag className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <p className="text-sm text-emerald-300">You have <span className="font-bold text-emerald-400">{totalRemaining} sessions</span> remaining across your packages.</p>
        </div>
      )}

      {/* View switcher */}
      <div className="flex gap-2 overflow-x-auto scrollbar-hide">
        {[
          { id: "browse", label: "Book Session", icon: Calendar },
          { id: "my-bookings", label: "My Bookings", icon: Check, badge: pendingBookings.length + confirmedBookings.length },
          { id: "packages", label: "Packages", icon: Package },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setView(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              view === tab.id ? "bg-primary text-white" : "bg-[#1E293B] text-gray-400 hover:text-white"
            }`}
            data-testid={`schedule-view-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && <span className="bg-white/20 text-[10px] px-1.5 py-0.5 rounded-full">{tab.badge}</span>}
          </button>
        ))}
      </div>

      {/* ─── BROWSE SLOTS ─── */}
      {view === "browse" && (
        <div className="space-y-4">
          {sessionTypes.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {sessionTypes.map(st => (
                <div key={st.type_id} className="bg-[#141B2D] border border-[#1E293B] rounded-xl p-3">
                  <p className="font-semibold text-white text-sm">{st.name}</p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                    <span className="bg-primary/20 text-primary px-2 py-0.5 rounded-full">{st.category}</span>
                    <span><Clock className="w-3 h-3 inline" /> {st.duration_minutes}m</span>
                    {st.price > 0 && <span className="text-emerald-400">${st.price}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Week calendar */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <button onClick={() => setWeekOffset(w => w - 1)} className="p-2 rounded-lg bg-[#1E293B] hover:bg-[#2D3B4F] transition-colors" data-testid="week-prev">
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>
              <span className="text-sm font-medium text-white">{weekDates[0]} — {weekDates[6]}</span>
              <button onClick={() => setWeekOffset(w => w + 1)} className="p-2 rounded-lg bg-[#1E293B] hover:bg-[#2D3B4F] transition-colors" data-testid="week-next">
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-3">
              {weekDates.map((date, i) => {
                const daySlots = (slotsByDate[date] || []).sort((a, b) => a.start_time.localeCompare(b.start_time));
                const isToday = date === new Date().toISOString().slice(0, 10);
                return (
                  <div key={date} className={`bg-[#141B2D] border rounded-xl p-3 min-h-[160px] ${isToday ? "border-primary/50" : "border-[#1E293B]"}`}>
                    <p className={`text-sm font-bold mb-0.5 ${isToday ? "text-primary" : "text-white/80"}`}>{DAYS[i]}</p>
                    <p className="text-xs text-white/40 mb-3">{date.slice(5)}</p>
                    <div className="space-y-1.5">
                      {daySlots.map(s => (
                        <button
                          key={s.slot_id}
                          onClick={() => setSelectedSlot(s)}
                          className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                            selectedSlot?.slot_id === s.slot_id
                              ? "bg-primary text-white"
                              : "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/30"
                          }`}
                          data-testid={`slot-${s.slot_id}`}
                        >
                          {formatTime(s.start_time)}
                        </button>
                      ))}
                      {daySlots.length === 0 && <p className="text-xs text-white/20 text-center pt-4">—</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {openSlots.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No available slots right now. Check back later.</p>
            </div>
          )}

          {/* Booking form */}
          {selectedSlot && (
            <div className="bg-[#141B2D] border border-primary/30 rounded-xl p-4 space-y-3" data-testid="booking-form">
              <h4 className="font-semibold text-white text-sm">Book This Slot</h4>
              <div className="flex items-center gap-3 text-sm text-gray-300">
                <span className="bg-primary/20 text-primary px-3 py-1 rounded-full font-medium">
                  {selectedSlot.date} at {formatTime(selectedSlot.start_time)}
                </span>
              </div>
              {sessionTypes.length > 0 && (
                <select
                  value={selectedTypeId}
                  onChange={(e) => setSelectedTypeId(e.target.value)}
                  className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="booking-session-type"
                >
                  <option value="">Select session type</option>
                  {sessionTypes.map(st => (
                    <option key={st.type_id} value={st.type_id}>
                      {st.name} — {st.duration_minutes}min {st.price > 0 ? `($${st.price})` : "(Free)"}
                    </option>
                  ))}
                </select>
              )}
              <textarea
                placeholder="Note for the coach (optional)"
                value={bookingNote}
                onChange={(e) => setBookingNote(e.target.value)}
                className="w-full bg-[#0B0F1A] border border-[#1E293B] rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
                data-testid="booking-note"
              />
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input type="checkbox" checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="rounded" data-testid="recurring-toggle" />
                Book this slot weekly for the next 4 weeks
              </label>
              <div className="flex gap-2">
                <button
                  onClick={handleBook}
                  className="bg-primary text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                  data-testid="submit-booking"
                >
                  Request Booking
                </button>
                <button
                  onClick={() => setSelectedSlot(null)}
                  className="bg-[#1E293B] text-gray-400 px-4 py-2 rounded-lg text-sm hover:bg-[#2D3B4F] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── MY BOOKINGS ─── */}
      {view === "my-bookings" && (
        <div className="space-y-6">
          {pendingBookings.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-amber-400 mb-3">Pending ({pendingBookings.length})</h4>
              <div className="space-y-2">
                {pendingBookings.map(b => (
                  <div key={b.booking_id} className="bg-[#141B2D] border border-amber-500/20 rounded-xl p-4" data-testid={`my-booking-${b.booking_id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white text-sm">{b.session_type_name || "Session"}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{b.date} at {formatTime(b.start_time)}</p>
                      </div>
                      <span className="text-[10px] bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full font-medium">Pending</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {confirmedBookings.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-emerald-400 mb-3">Confirmed ({confirmedBookings.length})</h4>
              <div className="space-y-2">
                {confirmedBookings.map(b => (
                  <div key={b.booking_id} className="bg-[#141B2D] border border-emerald-500/20 rounded-xl p-4" data-testid={`my-booking-${b.booking_id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-white text-sm">{b.session_type_name || "Session"}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{b.date} at {formatTime(b.start_time)}</p>
                        {b.session_deducted && <p className="text-[10px] text-emerald-400/70 mt-0.5">1 session used from: {b.package_name}</p>}
                      </div>
                      <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-full font-medium">Confirmed</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {pendingBookings.length === 0 && confirmedBookings.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No bookings yet. Browse available slots to book a session.</p>
            </div>
          )}
        </div>
      )}

      {/* ─── PACKAGES ─── */}
      {view === "packages" && (
        <div className="space-y-6">
          {/* My Purchased Packages */}
          {myPurchases.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-emerald-400 mb-3">My Purchased Packages</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {myPurchases.map(p => (
                  <div key={p.purchase_id} className="bg-[#141B2D] border border-emerald-500/20 rounded-xl p-4" data-testid={`my-purchase-${p.purchase_id}`}>
                    <p className="font-bold text-white text-sm">{p.package_name}</p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-emerald-400 font-bold text-lg">{p.sessions_remaining}</span>
                      <span className="text-xs text-gray-400">sessions remaining</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Available Packages */}
          <div>
            <h4 className="text-sm font-semibold text-white mb-3">Available Training Packages</h4>
            {packages.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No training packages available yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {packages.map(pkg => (
                  <div key={pkg.package_id} className="bg-[#141B2D] border border-[#1E293B] rounded-xl p-5 hover:border-primary/30 transition-colors" data-testid={`athlete-package-${pkg.package_id}`}>
                    <p className="font-bold text-white">{pkg.name}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                        {pkg.package_type === "monthly" ? "Monthly" : "Session Pack"}
                      </span>
                      <span><Users className="w-3 h-3 inline" /> {pkg.sessions_included} sessions</span>
                    </div>
                    <p className="text-2xl font-bold text-emerald-400 mt-3">${pkg.price}</p>
                    {pkg.duration_label && <p className="text-xs text-gray-500">{pkg.duration_label}</p>}
                    {pkg.description && <p className="text-xs text-gray-400 mt-2">{pkg.description}</p>}
                    {pkg.price > 0 && (
                      <button
                        onClick={() => setCheckoutPkg(pkg)}
                        className="mt-4 w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors"
                        data-testid={`buy-package-${pkg.package_id}`}
                      >
                        <CreditCard className="w-4 h-4" />
                        Purchase Package
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {checkoutPkg && (
        <PackageCheckoutModal
          isOpen={!!checkoutPkg}
          onClose={() => setCheckoutPkg(null)}
          packageData={checkoutPkg}
        />
      )}
    </div>
  );
};

export { AthleteScheduling };
export default AthleteScheduling;
